import React, { useEffect, useState, useRef } from 'react';
const FuturisticCursor = () => {
  const [position, setPosition] = useState({
    x: 0,
    y: 0
  });
  const [hidden, setHidden] = useState(true);
  const [clicked, setClicked] = useState(false);
  const [linkHovered, setLinkHovered] = useState(false);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const touchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    // Check if device is mobile or tablet
    const checkDevice = () => {
      const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
      const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
      const isTablet = /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(userAgent.toLowerCase());
      setIsMobileOrTablet(isMobile || isTablet);
    };
    checkDevice();
    const updatePosition = (e: MouseEvent) => {
      setPosition({
        x: e.clientX,
        y: e.clientY
      });
      setHidden(false);
    };
    const handleTouch = (e: TouchEvent) => {
      if (e.touches && e.touches[0]) {
        setPosition({
          x: e.touches[0].clientX,
          y: e.touches[0].clientY
        });
        setHidden(false);
        setIsTouched(true);
        setClicked(true);
        // Clear any existing timeout
        if (touchTimeoutRef.current) {
          clearTimeout(touchTimeoutRef.current);
        }
        // Set timeout to hide cursor after 500ms on mobile/tablet (faster fadeout)
        touchTimeoutRef.current = setTimeout(() => {
          setIsTouched(false);
          setClicked(false);
          setHidden(true); // Hide the cursor completely after timeout
        }, 500);
      }
    };
    const handleMouseDown = () => setClicked(true);
    const handleMouseUp = () => setClicked(false);
    const handleLinkHoverOn = (e: MouseEvent) => {
      if ((e.target as HTMLElement).tagName === 'A' || (e.target as HTMLElement).tagName === 'BUTTON' || (e.target as HTMLElement).closest('a') || (e.target as HTMLElement).closest('button')) {
        setLinkHovered(true);
      }
    };
    const handleLinkHoverOff = () => {
      setLinkHovered(false);
    };
    const handleMouseLeave = () => setHidden(true);
    const handleMouseEnter = () => setHidden(false);
    // Add desktop event listeners
    document.addEventListener('mousemove', updatePosition);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mouseover', handleLinkHoverOn);
    document.addEventListener('mouseout', handleLinkHoverOff);
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseenter', handleMouseEnter);
    // Add mobile/tablet event listeners
    document.addEventListener('touchstart', handleTouch);
    document.addEventListener('touchmove', handleTouch);
    return () => {
      // Remove desktop event listeners
      document.removeEventListener('mousemove', updatePosition);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mouseover', handleLinkHoverOn);
      document.removeEventListener('mouseout', handleLinkHoverOff);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseenter', handleMouseEnter);
      // Remove mobile/tablet event listeners
      document.removeEventListener('touchstart', handleTouch);
      document.removeEventListener('touchmove', handleTouch);
      // Clear any existing timeout
      if (touchTimeoutRef.current) {
        clearTimeout(touchTimeoutRef.current);
      }
    };
  }, []);
  if (typeof window === 'undefined') return null;
  // Don't render the persistent cursor on mobile/tablet, only show on touch
  if (isMobileOrTablet && !isTouched) {
    return null;
  }
  return <>
      <div className={`cursor-dot fixed pointer-events-none z-[9999] transition-all duration-100 ${hidden ? 'opacity-0' : 'opacity-100'} ${clicked ? 'scale-50' : 'scale-100'} ${linkHovered ? 'scale-150' : 'scale-100'} ${isMobileOrTablet ? 'transition-opacity duration-300 opacity-70' : ''}`} style={{
      left: `${position.x}px`,
      top: `${position.y}px`,
      width: isMobileOrTablet ? '16px' : '8px',
      height: isMobileOrTablet ? '16px' : '8px',
      backgroundColor: linkHovered ? '#ec4899' : '#ffffff',
      borderRadius: '50%',
      transform: 'translate(-50%, -50%)',
      mixBlendMode: 'difference',
      boxShadow: linkHovered ? '0 0 10px #ec4899, 0 0 20px #ec4899' : 'none'
    }} data-id="element-227" />
      <div className={`cursor-ring fixed pointer-events-none z-[9998] transition-all duration-300 ${hidden ? 'opacity-0' : 'opacity-100'} ${clicked ? 'scale-150 opacity-30' : 'scale-100'} ${linkHovered ? 'scale-200 opacity-50' : 'scale-100'} ${isMobileOrTablet ? 'transition-opacity duration-300 opacity-50' : ''}`} style={{
      left: `${position.x}px`,
      top: `${position.y}px`,
      width: isMobileOrTablet ? '40px' : '32px',
      height: isMobileOrTablet ? '40px' : '32px',
      border: `1px solid ${linkHovered ? '#ec4899' : 'rgba(255, 255, 255, 0.5)'}`,
      borderRadius: '50%',
      transform: 'translate(-50%, -50%)',
      transition: 'width 0.3s, height 0.3s, border-color 0.3s, opacity 0.3s'
    }} data-id="element-228" />
      <style jsx global data-id="element-229">{`
        body {
          cursor: ${isMobileOrTablet ? 'auto' : 'none'} !important;
        }
        a,
        button,
        [role='button'],
        input,
        select,
        textarea {
          cursor: ${isMobileOrTablet ? 'auto' : 'none'} !important;
        }
      `}</style>
    </>;
};
export default FuturisticCursor;